<script setup>
import { ref, reactive, computed } from 'vue';
import { useShared } from '../state.js';
import { formatTimeAgo, useNowTicker } from '../use_relative_time.js';
import RecomputeButton from './recompute_button.vue';

const { inputs, recompute } = useShared();

function onSubmit() {
  console.log("sight settings submit");
  recompute();
}

// Only the field's own "updated 3m ago" label timestamp — state.js's
// auto-touch watcher handles the setup's sync timestamp on its own for
// every edit, so there's nothing else to do here.
function touchSightSettings() {
  inputs.sight_settings.updated_at = Date.now();
}

const now = useNowTicker();
const updatedAgo = computed(() => {
  now.value; // re-evaluate as time passes, not just when updated_at changes
  return formatTimeAgo(inputs.sight_settings.updated_at);
});
</script>

<template>
  <div class="centered">
    <div class="form-grid">
      <label>
        Eye/Peep to Sight (mm)
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.eye_to_sight"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
      <label>
        Arrow to Eye/Peep (mm)
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.arrow_to_eye"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
      <label>
        Sight bar position
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.sight_bar_position"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
    </div>

    <hr class="settings-divider" />

    <div class="form-grid">
      <label>Sight Scale
        <select v-model.number="inputs.sight_settings.sight_scale"
          @change="[touchSightSettings, onSubmit]" >
          <option value="10">0-10</option>
          <option value="1">0-100</option>
        </select>
      </label>
      <label>
        Sight TPI Vertical
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.vertical_tpi"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
      <label>
        Sight TPI Horizontal
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.horizontal_tpi"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
      <label>
        Clicks per turn
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.clicks_per_turn"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
      <label>
        Sight bar spacing (mm)
        <input type="number" class="input-measure" required
          v-model.number="inputs.sight_settings.sight_bar_spacing"
          @input="touchSightSettings"
          @keyup.enter="onSubmit" />
      </label>
    </div>

    <RecomputeButton />

    <p v-if="updatedAgo" class="updated-ago">Updated {{ updatedAgo }}</p>
  </div>
</template>

<style scoped>

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-md);
}

label {
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-sm);
  gap: var(--space-xs);
}

/* An odd item count leaves the last label alone on its row (2-column
   grid) — span it across both columns and center it instead of leaving
   it stuck flush left. */
.form-grid label:last-child:nth-child(odd) {
  grid-column: 1 / -1;
  align-items: center;
}

.settings-divider {
  width: 100%;
  border: none;
  border-top: var(--border-width) solid var(--color-border);
  margin: 0;
}

.updated-ago {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  opacity: 0.6;
}

</style>
